import React, { useState, useEffect, useMemo, useRef } from "react";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { getIssueTypes, userApi } from "../../../api";
import CollapseSection from "../../../components/CollaspeSection";
import { getDateFormat } from "../../../utils/date-format";
import { useQuery } from "@tanstack/react-query";

import { Template } from "devextreme-react/core/template";
import UserSelectBoxItem from "../../../components/SelectBoxItems/UserSelectBoxItem";
import {
  applyTimeIntervalSelection,
  cloneReportInfoForForm,
  getTimeIntervalVisibility,
} from "./report-params-utils";
import {
  ALL_SELECTED_FILTERS_KEY,
  UNASSIGNED_FILTER_VALUE,
  displayWithUnassigned,
  unassignedOption,
  unassignedUserOption,
} from "./filter-constants";

const STATUS_OPTIONS = [
  { value: "Yes", text: "Resolved" },
  { value: "No", text: "Not Resolved" },
];

const QaEntryParamsForm = ({ paramsForm, reportInfo }) => {
  const [editorVisiblity, setEditorVisbility] = useState(() => {
    const { startDate, endDate } = getTimeIntervalVisibility(
      reportInfo?.report_parameters?.time_interval
    );
    return { startDate, endDate };
  });

  let startDateRef = null;
  let endDateRef = null;

  const { data: users } = useQuery({
    queryKey: ["users", { includeObservers: false }],
    queryFn: () => userApi.getUsers({ includeObservers: false }),
    initialData: [],
    select: (data) =>
      data.map((user) => ({
        user_id: user.user_id,
        email: user.email,
        full_name: user.full_name,
        title: user.title,
      })),
    refetchOnWindowFocus: false,
  });

  const { data: issueTypes } = useQuery({
    queryKey: ["qa:issueTypes"],
    queryFn: () => getIssueTypes(),
    initialData: [],
    refetchOnWindowFocus: false,
  });

  // Option lists are memoized so the seeding effects below can set tag box
  // values with the same object identities the editors' items use (required
  // for the user object tag boxes, which have no valueExpr). Examiner and
  // reviewer each get their own array because their sentinel labels differ.
  // Status has no unassigned sentinel: every QA entry has a resolved state.
  const examinerOptions = useMemo(
    () => [unassignedUserOption("(No Examiner)"), ...users],
    [users]
  );

  const reviewerOptions = useMemo(
    () => [unassignedUserOption("(No Reviewer)"), ...users],
    [users]
  );

  const issueTypeOptions = useMemo(
    () => [unassignedOption("issue_type"), ...issueTypes],
    [issueTypes]
  );

  // Fields saved as "all selected" reseed their tag box fully checked once
  // the lookup resolves. Values are derived from the current options, not a
  // snapshot, so options added since the save show up checked too.
  const allSelectedFilters = useMemo(
    () => reportInfo?.report_parameters?.[ALL_SELECTED_FILTERS_KEY] || [],
    [reportInfo]
  );

  const seededRef = useRef({});
  const seedAllSelected = (paramKey, values) => {
    if (seededRef.current[paramKey]) return;
    if (!allSelectedFilters.includes(paramKey)) return;
    // A sentinel bearing field carries its "__unassigned__" option
    // synchronously, before the lookup resolves, so wait for at least one real
    // (non sentinel) value before seeding. Seeding and locking on the sentinel
    // only state would strand the field on "(No ...)".
    const hasRealValue = values.some((v) =>
      v && typeof v === "object"
        ? !Object.values(v).includes(UNASSIGNED_FILTER_VALUE)
        : v !== UNASSIGNED_FILTER_VALUE
    );
    if (!hasRealValue) return;
    // Seed each all selected field once. Re-running on later option identity
    // changes (e.g. a shared query refetch) would reset the tag box to fully
    // checked and clobber a partial deselection the user made mid edit.
    seededRef.current[paramKey] = true;
    paramsForm.current?.instance?.updateData(
      `report_parameters.${paramKey}`,
      values
    );
  };

  useEffect(() => {
    seedAllSelected("examiner", examinerOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examinerOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected("reviewer", reviewerOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewerOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "issue_type",
      issueTypeOptions.map((i) => i.issue_type)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueTypeOptions, allSelectedFilters]);

  useEffect(() => {
    seedAllSelected(
      "status",
      STATUS_OPTIONS.map((s) => s.value)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSelectedFilters]);

  return (
    <>
      <CollapseSection
        title={"Report Parameters | QA Entries - Overview"}
        allowCollapse={false}
      >
        <Form
          ref={paramsForm}
          colCount={2}
          defaultFormData={cloneReportInfoForForm(reportInfo)}
        >
          <SimpleItem
            dataField="report_parameters.time_interval"
            label={{ text: "Time Interval", location: "top" }}
            colSpan={1}
            editorType="dxSelectBox"
            editorOptions={{
              items: [
                "All Time",
                "Custom",
                "Today",
                "Yesterday",
                "This Week",
                "Last Week",
                "This Month",
                "Last Month",
                "This Year",
                "Last Year",
              ],
              placeholder: "All Time",
              dropDownOptions: {
                maxHeight: 250,
              },
              onValueChanged: (e) => {
                const interval = applyTimeIntervalSelection(
                  e,
                  paramsForm.current?.instance,
                  []
                );
                const { startDate, endDate } =
                  getTimeIntervalVisibility(interval);
                setEditorVisbility({ ...editorVisiblity, startDate, endDate });
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.start_date"
            label={{ text: "Start Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.startDate}
            editorOptions={{
              onInitialized: (e) => {
                startDateRef = e.component;
              },
              onValueChanged: (e) => {
                endDateRef.option("min", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.end_date"
            label={{ text: "End Date", location: "top" }}
            editorType="dxDateBox"
            colSpan={1}
            isRequired={true}
            visible={editorVisiblity.endDate}
            editorOptions={{
              onInitialized: (e) => {
                endDateRef = e.component;
              },
              onValueChanged: (e) => {
                startDateRef.option("max", e.value);
              },
              type: "date",
              useMaskBehavior: true,
              displayFormat: getDateFormat({
                isMoment: false,
                includeTime: false,
              }),
            }}
          />
          <SimpleItem
            dataField="report_parameters.examiner"
            label={{ text: "Examiner" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              displayExpr: "full_name",
              items: examinerOptions,
              placeholder: "All Examiners",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
              itemTemplate: "report_parameters.examiner",
            }}
          />
          <Template
            name="report_parameters.examiner"
            render={(data) => <UserSelectBoxItem data={data} />}
          />
          <SimpleItem
            dataField="report_parameters.reviewer"
            label={{ text: "Reviewer" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              displayExpr: "full_name",
              items: reviewerOptions,
              placeholder: "All Reviewers",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
              itemTemplate: "report_parameters.reviewer",
            }}
          />
          <Template
            name="report_parameters.reviewer"
            render={(data) => <UserSelectBoxItem data={data} />}
          />
          <SimpleItem
            dataField="report_parameters.issue_type"
            label={{ text: "Issue Type" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "issue_type",
              displayExpr: displayWithUnassigned(
                "issue_type",
                "(No Issue Type)"
              ),
              items: issueTypeOptions,
              placeholder: "All Issue Types",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
          <SimpleItem
            dataField="report_parameters.status"
            label={{ text: "Status" }}
            colSpan={1}
            editorType="dxTagBox"
            editorOptions={{
              valueExpr: "value",
              displayExpr: "text",
              items: STATUS_OPTIONS,
              placeholder: "All Statuses",
              showClearButton: false,
              showDropDownButton: true,
              multiline: false,
              showSelectionControls: true,
              selectAllMode: "allPages",
              dropDownOptions: {
                maxHeight: 250,
              },
            }}
          />
        </Form>
      </CollapseSection>
    </>
  );
};

export default QaEntryParamsForm;
